import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Alert,
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { useAppColors } from '../../hooks/useAppColors';
import { responsiveContentStyle } from '../../hooks/useResponsiveLayout';
import { useAuthStore } from '../../stores/authStore';
import {
  useMembershipPlans,
  usePlanCatalog,
  useFamilyMemberships,
  useFamilyPaymentHistory,
  useInitiateOrder,
  useBatchChangeMembershipPlans,
  useScheduleMembershipCancel,
  useClearScheduledChange,
  type MembershipPlan,
} from '../../hooks/usePayments';
import { formatCurrency, formatDate, calculateAge } from '../../utils/formatters';
import LoadingIndicator from '../../components/common/LoadingIndicator';
import ScreenHeader from '../../components/common/ScreenHeader';
import SubUserForm from '../../components/billing/SubUserForm';
import PlanSelector from '../../components/billing/PlanSelector';
import TermSelector from '../../components/billing/TermSelector';
import PaymentHistoryList from '../../components/billing/PaymentHistoryList';
import {
  type SubUserFormData,
  calculatePlanTermPrice,
  calculateOfferPrice,
  getDiscountText,
  getMemberOffers,
  getOfferForPlanTerm,
  getAllowedTermsForMemberPlan,
  isDummyUUID,
  getNextDummyUUID,
} from '../../components/billing/billingUtils';
import {
  type PendingRenewals,
  basketTotal,
  buildRenewalItems,
  isEarlyRenewal,
  isRenewable,
  isRenewalTermValid,
  validateRenewals,
} from './renewalBasket';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useQueryClient } from '@tanstack/react-query';

type Tab = 'memberships' | 'payments';

// Prorated calculation — uses term_months from the membership's payment history
// Default to monthly (30 days) if no term history available

export default function MembershipManagementScreen() {
  const { t } = useTranslation();
  const colors = useAppColors();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<any>();
  const { width: screenWidth } = useWindowDimensions();
  const isNarrow = screenWidth < 400;
  const user = useAuthStore((s) => s.user);
  const setActiveVietQRPayment = useAuthStore((s) => s.setActiveVietQRPayment);
  const setNeedsSubscription = useAuthStore((s) => s.setNeedsSubscription);
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<Tab>('memberships');

  // Add member form state
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);

  // Renewal state
  const [renewingUserId, setRenewingUserId] = useState<string | null>(null);
  const [renewPlanId, setRenewPlanId] = useState<string | null>(null);

  // Plan-selector open state per membership_id (which card currently shows the picker)
  const [selectorOpenFor, setSelectorOpenFor] = useState<string | null>(null);

  // Pending (queued) plan changes — keyed by membership_id -> new_plan_id.
  // Not submitted until user clicks Pay at the bottom.
  const [pendingChanges, setPendingChanges] = useState<Record<string, string>>({});

  // Pending (queued) new members — not submitted until user clicks Pay.
  type PendingMember = {
    id: string;
    firstName: string;
    lastName: string;
    dob: Date;
    planId: string;
    termMonths: number;
    weightKg: number | null;
    heightCm: number | null;
    measurementUnit: 'metric' | 'imperial';
  };
  const [pendingMembers, setPendingMembers] = useState<PendingMember[]>([]);

  // Pending (queued) renewals keyed by user_id — paid together with new members in one order.
  const [pendingRenewals, setPendingRenewals] = useState<PendingRenewals>({});

  // Synchronous in-flight guard for Pay: React state updates are async, so two
  // fast presses could otherwise both reach the API before isPending flips.
  const payInFlight = useRef(false);

  // Term selection per member (for renewals and new members)
  const [memberTerms, setMemberTerms] = useState<Record<string, number>>({});

  // Contact dialog state
  const [showContactDialog, setShowContactDialog] = useState(false);

  const { data: basePlans, isLoading: plansLoading } = useMembershipPlans();
  const { data: familyMemberships, isLoading: famLoading } = useFamilyMemberships();

  // Build list of all family user IDs for catalog, including pending members and a draft for Add Member form
  const familyUserIds = React.useMemo(() => {
    const ids: string[] = [];
    if (user) ids.push(user.id);
    if (familyMemberships) {
      familyMemberships.forEach((fm: any) => {
        if (fm.user_id !== user?.id) ids.push(fm.user_id as string);
      });
    }
    // Include pending members (dummy UUIDs) so they get tier discounts
    pendingMembers.forEach((pm) => {
      ids.push(pm.id);
    });
    // Add a draft dummy ID for the Add Member form when it's open
    if (showAddMemberForm) {
      ids.push(getNextDummyUUID([...ids, ...pendingMembers.map(m => m.id)]));
    }
    return ids.length > 0 ? ids : [];
  }, [familyMemberships, user, pendingMembers, showAddMemberForm]);
  const { data: catalogData } = usePlanCatalog(familyUserIds.length > 0 ? familyUserIds : null);
  const catalogEntries = catalogData?.entries ?? [];

  // Merge inactive plans from catalog into the plans list
  const plans = React.useMemo(() => {
    if (!basePlans) return basePlans;
    const basePlanIds = new Set(basePlans.map((p: MembershipPlan) => p.id));
    // Find exclusive (hidden) plans from catalog that are targeted for a specific member
    const extraPlans: MembershipPlan[] = [];
    for (const entry of catalogEntries) {
      for (const offer of entry.offers) {
        if (offer.is_exclusive && !basePlanIds.has(offer.plan_id)) {
          extraPlans.push(offer.plan);
          basePlanIds.add(offer.plan_id);
        }
      }
    }
    return extraPlans.length > 0 ? [...basePlans, ...extraPlans] : basePlans;
  }, [basePlans, catalogEntries]);
  const { data: paymentHistory, isLoading: paymentsLoading } = useFamilyPaymentHistory();
  const initiateOrder = useInitiateOrder();
  const batchChangePlans = useBatchChangeMembershipPlans();
  const scheduleCancel = useScheduleMembershipCancel();
  const clearScheduled = useClearScheduledChange();

  const userId = user?.id || '';

  // ─── Initialize memberTerms from existing memberships ─────────────────────
  useEffect(() => {
    if (!familyMemberships) return;
    const initialTerms: Record<string, number> = {};
    familyMemberships.forEach((fm: any) => {
      const membership = fm.membership;
      if (membership) {
        // Use pending_renewal_term_months if set, otherwise use renewal_term_months, default to 1
        initialTerms[fm.user_id] = membership.pending_renewal_term_months || membership.renewal_term_months || 0;
      }
    });
    setMemberTerms(initialTerms);
  }, [familyMemberships]);

  // ─── Prorated amount helper (mirrors backend _compute_prorated_amount) ──
  const computeProrated = useCallback((oldPrice: number, newPrice: number, startDateStr: string | null | undefined, termMonths: number): number => {
    const cycleDays = termMonths * 30;  // Approximate days in term
    const delta = Math.max(newPrice - oldPrice, 0);
    if (delta === 0) return 0;
    if (!startDateStr) return delta;
    const start = new Date(startDateStr);
    const today = new Date();
    const msPerDay = 86400000;
    const elapsed = Math.max(Math.floor((today.getTime() - start.getTime()) / msPerDay), 0);
    const remaining = Math.max(cycleDays - elapsed, 0);
    let prorated = Math.round((delta * remaining) / cycleDays);
    if (delta > 0 && prorated <= 0) prorated = 1;
    return prorated;
  }, []);

  // ─── Add Member Handler (queues a pending member, no API call yet) ─────
  const handleAddMember = useCallback((data: SubUserFormData & { planId: string; termMonths: number }) => {
    if (!data.firstName.trim() || !data.lastName.trim() || !data.dob || !data.planId || !user) return;
    const newMember: PendingMember = {
      id: getNextDummyUUID(pendingMembers.map((m) => m.id)),
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      dob: data.dob,
      planId: data.planId,
      termMonths: data.termMonths,
      weightKg: data.weightKg,
      heightCm: data.heightCm,
      measurementUnit: data.measurementUnit,
    };
    setPendingMembers((prev) => [...prev, newMember]);
    // Also set the memberTerms for this new member so it shows correctly in Review Changes
    setMemberTerms((prev) => ({ ...prev, [newMember.id]: data.termMonths }));
    setShowAddMemberForm(false);
    setActiveTab('memberships');
  }, [user, pendingMembers]);

  const handleRemovePendingMember = useCallback((id: string) => {
    setPendingMembers((prev) => prev.filter((m) => m.id !== id));
  }, []);

  // ─── Queue a renewal locally (no API call) ─────────────────────────────
  const handleQueueRenewal = useCallback((memberUserId: string) => {
    if (!renewPlanId) return;
    const termMonths = memberTerms[memberUserId] ?? 0;
    setPendingRenewals((prev) => ({ ...prev, [memberUserId]: { planId: renewPlanId, termMonths } }));
    setRenewingUserId(null);
    setRenewPlanId(null);
  }, [renewPlanId, memberTerms]);

  const handleRemovePendingRenewal = useCallback((memberUserId: string) => {
    setPendingRenewals((prev) => {
      const next = { ...prev };
      delete next[memberUserId];
      return next;
    });
  }, []);

  // ─── Error helper ──────────────────────────────────────────────────────
  const showError = useCallback((err: any, fallbackKey = 'billing.paymentError') => {
    const detail = err?.response?.data?.detail;
    const msg = detail && typeof detail === 'string' && detail.startsWith('errors.')
      ? t(detail, { defaultValue: detail })
      : (detail || err?.message || t(fallbackKey));
    Alert.alert(t(fallbackKey), msg);
  }, [t]);

  // ─── Queue a plan change locally (no API call) ─────────────────────────
  const handleQueueChangePlan = useCallback((membershipId: string, newPlanId: string) => {
    setPendingChanges((prev) => ({ ...prev, [membershipId]: newPlanId }));
    setSelectorOpenFor(null);
  }, []);

  const handleRemovePendingChange = useCallback((membershipId: string) => {
    setPendingChanges((prev) => {
      const next = { ...prev };
      delete next[membershipId];
      return next;
    });
  }, []);

  // ─── Submit all queued changes + renewals + new members in one payment flow ───────
  type NavigatePayment = {
    payment_id: string;
    order_number: string;
    amount: string;
    qr_url: string;
    bank_bin: string;
    account_number: string;
    account_name: string;
    status?: string;
    order_items?: { label: string; description: string; subtotal: string; discount_amount: string; final_amount: string }[];
  };

  const goToPayment = useCallback((p: NavigatePayment) => {
    if (p.status === 'completed') {
      setActiveVietQRPayment(null);
      setNeedsSubscription(false);
      queryClient.invalidateQueries();
      return;
    }
    const order_items = (p.order_items || []).map((item) => ({
      ...item,
      description: item.description
        ? t(`plans.${item.description}.name`, { defaultValue: item.description })
        : '',
    }));
    setActiveVietQRPayment({
      payment_id: p.payment_id,
      order_number: p.order_number,
      amount: parseFloat(p.amount),
      qr_url: p.qr_url,
      bank_bin: p.bank_bin,
      account_number: p.account_number,
      account_name: p.account_name,
      order_items,
    });
    navigation.navigate('VietQR', {
      payment_id: p.payment_id,
      order_number: p.order_number,
      amount: p.amount,
      qr_url: p.qr_url,
      bank_bin: p.bank_bin,
      account_number: p.account_number,
      account_name: p.account_name,
      order_items,
    });
  }, [setActiveVietQRPayment, setNeedsSubscription, queryClient, navigation, t]);

  const handlePayPendingChanges = useCallback(async () => {
    if (payInFlight.current) return;
    const changeEntries = Object.entries(pendingChanges);
    const hasChanges = changeEntries.length > 0;
    const hasMembers = pendingMembers.length > 0;
    const hasRenewals = Object.keys(pendingRenewals).length > 0;
    if (!hasChanges && !hasMembers && !hasRenewals) return;

    // Revalidate queued renewals against CURRENT memberships/plans/terms before charging.
    if (hasRenewals) {
      const { valid, dropped } = validateRenewals(pendingRenewals, familyMemberships, plans, catalogEntries, new Date());
      if (dropped.length > 0) {
        setPendingRenewals(valid);
        Alert.alert(t('mgmt.pendingChangesTitle'), t('mgmt.renewalsDropped'));
        return;
      }
    }
    const renewalItems = buildRenewalItems(pendingRenewals);

    payInFlight.current = true;
    // Payment produced by the plan-change step; shown even if the order step fails.
    let changePayment: NavigatePayment | null = null;
    try {
      // 1. Queue plan changes (prorated upgrades create a payment; downgrades are scheduled only)
      if (hasChanges) {
        const result = await batchChangePlans.mutateAsync({
          changes: changeEntries.map(([membership_id, new_plan_id]) => ({ membership_id, new_plan_id })),
          language: i18n.language,
        });
        setPendingChanges({});
        if (result.payment) {
          changePayment = result.payment;
        }
      }

      // 2. Renewals + new member purchases go in ONE order (always creates a payment)
      let orderPayment: NavigatePayment | null = null;
      if (hasMembers || hasRenewals) {
        try {
          orderPayment = await initiateOrder.mutateAsync({
            ...(hasRenewals ? { memberships: renewalItems } : {}),
            ...(hasMembers ? {
              pending_members: pendingMembers.map((m) => ({
                first_name: m.firstName,
                last_name: m.lastName,
                dob: m.dob.toISOString().split('T')[0],
                weight_kg: m.weightKg,
                height_cm: m.heightCm,
                measurement_unit: m.measurementUnit,
                plan_id: m.planId,
                term_months: m.termMonths || 1,
              })),
            } : {}),
            language: i18n.language,
          });
          setPendingMembers([]);
          setPendingRenewals({});
        } catch (err: any) {
          // Plan changes already went through: surface their payment (if any) so the
          // user can still pay it, and keep the renewals/members queued for a retry.
          showError(err);
          if (changePayment) goToPayment(changePayment);
          return;
        }
      }

      // Prefer the order payment for navigation (a change payment stays pending and
      // visible in payment history).
      const navigatePayment = orderPayment ?? changePayment;
      if (navigatePayment) {
        goToPayment(navigatePayment);
      } else {
        // All changes were downgrades — already scheduled, no charge
        Alert.alert(
          t('mgmt.scheduledAppliedTitle'),
          t('mgmt.scheduledAppliedMessage'),
        );
      }
    } catch (err: any) {
      showError(err);
    } finally {
      payInFlight.current = false;
    }
  }, [pendingChanges, pendingMembers, pendingRenewals, plans, familyMemberships, catalogEntries, batchChangePlans, initiateOrder, goToPayment, t, showError]);

  // ─── Contact Us Handler (for Change Plan & Cancel Membership) ─────────
  const handleContactForChanges = useCallback(() => {
    setShowContactDialog(true);
  }, []);

  // ─── Cancel Membership Handler ─────────────────────────────────────────
  const handleCancelMembership = useCallback((membershipId: string) => {
    handleContactForChanges();
  }, [handleContactForChanges]);

  // ─── Undo scheduled change ─────────────────────────────────────────────
  const handleClearScheduled = useCallback(async (membershipId: string) => {
    try {
      await clearScheduled.mutateAsync(membershipId);
    } catch (err) {
      showError(err);
    }
  }, [clearScheduled, showError]);

  // ─── Early return for loading (after all hooks) ────────────────────────
  if (plansLoading || famLoading) return <LoadingIndicator />;

  // ─── Tab bar ───────────────────────────────────────────────────────────
  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: 'memberships', label: t('mgmt.membershipsTab'), icon: 'card-account-details' },
    { key: 'payments', label: t('mgmt.paymentsTab'), icon: 'receipt-text' },
  ];

  return (
    <View style={[styles.screen, { backgroundColor: colors.bg }]}>
      <ScreenHeader
        title={t('mgmt.title')}
        onBack={() => navigation.goBack()}
      />

      {/* Tab bar */}
      <View style={[styles.tabBar, { backgroundColor: colors.card, borderBottomColor: colors.border }]}>
        <View style={[styles.tabBarInner, responsiveContentStyle]}>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tabItem, isActive && { borderBottomColor: colors.accent }]}
              onPress={() => setActiveTab(tab.key)}
              activeOpacity={0.7}
            >
              <Icon name={tab.icon} size={18} color={isActive ? colors.accent : colors.textTertiary} />
              <Text style={[styles.tabLabel, { color: isActive ? colors.accent : colors.textTertiary }]}>
                {tab.label}
              </Text>
            </TouchableOpacity>
          );
        })}
        </View>
      </View>

      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={{ flex: 1 }}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[styles.scrollContent, responsiveContentStyle, { paddingBottom: insets.bottom + 24 }]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {activeTab === 'memberships' && (
          /* ══════════════ MEMBERSHIPS TAB ══════════════ */
          <View style={styles.tabContent}>
            {familyMemberships && familyMemberships.length > 0 ? (
              familyMemberships.map((fm: any) => {
                const isSelf = fm.user_id === userId;
                const membership = fm.membership;
                const plan = membership?.plan;
                const isActive = membership?.status === 'active';
                const isExpired = membership?.status === 'expired';
                const isCancelled = membership?.status === 'cancelled';
                // Renewable = expired/cancelled, or active/paused ending within the renew window
                const today = new Date();
                const renewable = isRenewable(membership, today);
                const earlyRenewal = isEarlyRenewal(membership, today);
                const queuedRenewal = pendingRenewals[fm.user_id];

                // Renew pricing (computed outside JSX to avoid IIFE-in-JSX parse errors)
                const renewTerm = memberTerms[fm.user_id] ?? 0;
                const renewSelectedPlan = renewPlanId ? plans?.find((p: MembershipPlan) => p.id === renewPlanId) : undefined;
                const memberOffers = getMemberOffers(catalogEntries, fm.user_id);
                const renewAllowedTerms = renewSelectedPlan
                  ? getAllowedTermsForMemberPlan(memberOffers, renewSelectedPlan.id)
                  : [3, 6, 12];
                const renewValidTerm = renewAllowedTerms.includes(renewTerm);
                const renewOffer = (renewSelectedPlan && renewValidTerm)
                  ? getOfferForPlanTerm(memberOffers, renewSelectedPlan.id, renewTerm)
                  : null;
                const renewPricing = renewSelectedPlan && renewValidTerm
                  ? calculateOfferPrice(renewSelectedPlan, renewTerm, renewOffer)
                  : null;
                const renewHasSavings = renewPricing ? renewPricing.discountAmount > 0 : false;

                // Active membership pricing display (computed outside JSX)
                const termMonths = membership?.billing_details?.term_months || membership?.renewal_term_months || 1;
                const activeOffer = plan ? getOfferForPlanTerm(memberOffers, plan.id, termMonths) : null;
                const activePricing = plan ? calculateOfferPrice(plan, termMonths, activeOffer) : null;
                const activeHasSavings = activePricing ? activePricing.discountAmount > 0 : false;
                const activeFinalAmount = membership?.billing_details?.line_amount != null
                  ? parseFloat(membership.billing_details.line_amount)
                  : activePricing?.finalAmount || 0;

                return (
                  <View
                    key={fm.user_id}
                    style={[styles.memberCard, { backgroundColor: colors.card, borderColor: colors.border }]}
                  >
                    {/* Member header */}
                    <View style={styles.memberHeader}>
                      <View style={[styles.memberAvatar, { backgroundColor: colors.border }]}>
                        <Icon name={isSelf ? 'account' : 'account-child'} size={22} color={colors.textSecondary} />
                      </View>
                      <View style={styles.memberInfo}>
                        <Text style={[styles.memberName, { color: colors.text }]}>{fm.user_name}</Text>
                        {fm.dob && (
                          <Text style={[styles.memberDob, { color: colors.textTertiary }]}>
                            {t('profile.age', { count: calculateAge(fm.dob) })}
                          </Text>
                        )}
                      </View>
                    </View>

                    {/* Membership status */}
                    {plan ? (
                      <View style={[styles.planRow, { backgroundColor: colors.bg, borderColor: colors.border }]}>
                        <View style={{ flex: 1 }}>
                          {/* Header: Plan name + Status badge */}
                          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Text style={[styles.planName, { color: colors.text, flex: 1, marginRight: 8 }]}>
                              {t(`plans.${plan.machine_name}.name`, { defaultValue: plan.machine_name })}
                            </Text>
                            <View style={[
                              styles.statusBadge,
                              {
                                backgroundColor: isActive ? 'rgba(34,197,94,0.12)' : isExpired ? 'rgba(239,68,68,0.12)' : 'rgba(156,163,175,0.12)',
                              },
                            ]}>
                              <View style={[
                                styles.statusDot,
                                { backgroundColor: isActive ? '#22c55e' : isExpired ? '#ef4444' : '#9ca3af' },
                              ]} />
                              <Text style={[
                                styles.statusText,
                                { color: isActive ? '#22c55e' : isExpired ? '#ef4444' : '#9ca3af' },
                              ]}>
                                {isActive ? t('membershipStatus.active') : isExpired ? t('membershipStatus.expired') : isCancelled ? t('membershipStatus.cancelled') : t('membershipStatus.paused')}
                              </Text>
                            </View>
                          </View>

                          {/* Two-column: Pricing (left) + Dates (right) */}
                          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 8 }}>
                            {/* Left: Pricing */}
                            <View style={{ flex: 1 }}>
                              {activeHasSavings && activePricing && activePricing.discount && (
                                <>
                                  <Text style={{ fontSize: 11, color: colors.textTertiary, textDecorationLine: 'line-through' }}>
                                    {formatCurrency(activePricing.subtotal)}
                                  </Text>
                                  <Text style={{ fontSize: 13, color: colors.accent, fontWeight: '600' }}>
                                    {formatCurrency(activeFinalAmount)} / {termMonths} {t('billing.months', { defaultValue: 'months' })}
                                  </Text>
                                  <Text style={[styles.planDiscount, { color: colors.accent, marginTop: 2 }]}>
                                    {getDiscountText(activePricing.discount, activePricing.subtotal, activePricing.finalAmount, formatCurrency, t, termMonths)}
                                  </Text>
                                </>
                              )}
                              {!activeHasSavings && (
                                <Text style={{ fontSize: 13, color: colors.textSecondary, fontWeight: '600' }}>
                                  {formatCurrency(activeFinalAmount)} / {termMonths} {t('billing.months', { defaultValue: 'months' })}
                                </Text>
                              )}
                            </View>
                            {/* Right: Dates */}
                            <View style={{ alignItems: 'flex-end' }}>
                              {membership.start_date && (
                                <Text style={{ fontSize: 11, color: colors.textTertiary }}>
                                  {t('mgmt.started', { date: formatDate(membership.start_date, t('currentLanguage', { defaultValue: 'en' })) })}
                                </Text>
                              )}
                              {membership.end_date && (
                                <Text style={{ fontSize: 11, color: colors.textTertiary }}>
                                  {isExpired
                                    ? t('mgmt.ended', { date: formatDate(membership.end_date, t('currentLanguage', { defaultValue: 'en' })) })
                                    : t('mgmt.ends', { date: formatDate(membership.end_date, t('currentLanguage', { defaultValue: 'en' })) })}
                                </Text>
                              )}
                            </View>
                          </View>
                        </View>

                        {/* Already queued in the payment basket */}
                        {queuedRenewal && (
                          <View style={styles.queuedRenewal}>
                            <Text style={[styles.queuedRenewalText, { color: colors.accent }]}>{t('mgmt.queuedForPayment')}</Text>
                            <TouchableOpacity onPress={() => handleRemovePendingRenewal(fm.user_id)} hitSlop={8}>
                              <Text style={[styles.scheduledAction, { color: '#ef4444' }]}>{t('mgmt.remove')}</Text>
                            </TouchableOpacity>
                          </View>
                        )}
                        {/* Renew button for renewable memberships — show initial button */}
                        {renewable && !queuedRenewal && !(renewingUserId === fm.user_id && renewPlanId) && (
                          <TouchableOpacity
                            style={[styles.renewBtn, { backgroundColor: colors.accent }]}
                            onPress={() => setRenewingUserId(fm.user_id)}
                            activeOpacity={0.7}
                          >
                            <Text style={[styles.renewBtnText, { color: colors.accentText }]}>
                              {earlyRenewal ? t('mgmt.renewEarly') : t('mgmt.renew')}
                            </Text>
                          </TouchableOpacity>
                        )}
                        {/* Renew confirm for renewable memberships after plan selected */}
                        {renewable && !queuedRenewal && renewingUserId === fm.user_id && renewPlanId && (
                          <View style={styles.renewConfirm}>
                            <TermSelector
                              selectedTermMonths={renewTerm}
                              onSelectTerm={(months) => setMemberTerms(prev => ({ ...prev, [fm.user_id]: months }))}
                              compact
                              allowedTermMonths={renewSelectedPlan
                                ? getAllowedTermsForMemberPlan(memberOffers, renewSelectedPlan.id)
                                : undefined}
                            />
                            {!renewValidTerm && (
                              <Text style={{ fontSize: 11, color: '#ef4444', alignSelf: 'flex-start', marginTop: -2 }}>
                                {t('billing.selectTermRequired', { defaultValue: 'Please select a term to continue' })}
                              </Text>
                            )}
                            {renewPricing && (
                              <View style={[styles.pricingBreakdown, { backgroundColor: colors.bg, borderColor: colors.border }]}>
                                <View style={styles.pricingBreakdownRow}>
                                  <View style={{ flex: 1 }}>
                                    <Text style={[styles.pricingBreakdownPlan, { color: colors.text }]}>
                                      {renewSelectedPlan ? t(`plans.${renewSelectedPlan.machine_name}.name`, { defaultValue: renewSelectedPlan.machine_name }) : ''}
                                    </Text>
                                    <Text style={[styles.pricingBreakdownTerm, { color: colors.textTertiary }]}>
                                      {renewTerm} {renewTerm === 1 ? t('common.month') : t('common.months')}
                                    </Text>
                                    {renewHasSavings && renewPricing.discount && (
                                      <Text style={[styles.pricingBreakdownDiscount, { color: colors.accent }]}>
                                        {getDiscountText(renewPricing.discount, renewPricing.subtotal, renewPricing.finalAmount, formatCurrency, t, renewTerm)}
                                      </Text>
                                    )}
                                  </View>
                                  <View style={{ alignItems: 'flex-end' }}>
                                    {renewHasSavings && (
                                      <Text style={[styles.pricingBreakdownStrike, { color: colors.textTertiary }]}>
                                        {formatCurrency(renewPricing.subtotal)}
                                      </Text>
                                    )}
                                    <Text style={[styles.pricingBreakdownFinal, { color: colors.text }]}>
                                      {formatCurrency(renewPricing.finalAmount)}
                                    </Text>
                                    <Text style={[styles.pricingBreakdownPerMonth, { color: colors.textSecondary }]}>
                                      {formatCurrency(renewPricing.finalAmount / renewTerm)}/{t('billing.perMonth')}
                                    </Text>
                                  </View>
                                </View>
                              </View>
                            )}
                            <TouchableOpacity
                              style={[styles.confirmRenewBtn, { backgroundColor: colors.accent, opacity: !renewValidTerm ? 0.5 : 1 }]}
                              onPress={() => handleQueueRenewal(fm.user_id)}
                              disabled={!renewValidTerm}
                              activeOpacity={0.7}
                            >
                              <Text style={[styles.confirmRenewBtnText, { color: colors.accentText }]}>
                                {t('mgmt.addToPayment')}
                              </Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                              onPress={() => { setRenewingUserId(null); setRenewPlanId(null); }}
                              hitSlop={12}
                            >
                              <Text style={[styles.cancelRenewText, { color: colors.textSecondary }]}>{t('common.cancel')}</Text>
                            </TouchableOpacity>
                          </View>
                        )}
                      </View>
                    ) : (
                      <View style={[styles.noPlanRow, { backgroundColor: colors.bg, borderColor: colors.border }]}>
                        <Icon name="alert-circle-outline" size={18} color={colors.textTertiary} />
                        <Text style={[styles.noPlanText, { color: colors.textTertiary }]}>{t('profile.noPlan')}</Text>
                        {queuedRenewal ? (
                          <View style={styles.queuedRenewal}>
                            <Text style={[styles.queuedRenewalText, { color: colors.accent }]}>{t('mgmt.queuedForPayment')}</Text>
                            <TouchableOpacity onPress={() => handleRemovePendingRenewal(fm.user_id)} hitSlop={8}>
                              <Text style={[styles.scheduledAction, { color: '#ef4444' }]}>{t('mgmt.remove')}</Text>
                            </TouchableOpacity>
                          </View>
                        ) : (
                          <TouchableOpacity
                            style={[styles.addPlanBtn, { backgroundColor: colors.accent }]}
                            onPress={() => setRenewingUserId(fm.user_id)}
                            activeOpacity={0.7}
                          >
                            <Text style={[styles.addPlanBtnText, { color: colors.accentText }]}>{t('mgmt.addPlan')}</Text>
                          </TouchableOpacity>
                        )}
                      </View>
                    )}

                    {/* Renewal plan selector (shown when renewing or adding plan) */}
                    {renewingUserId === fm.user_id && !renewPlanId && (
                      <View style={styles.planSelectorWrap}>
                        <Text style={[styles.planSelectorLabel, { color: colors.textTertiary }]}>
                          {t('mgmt.selectPlan')}
                        </Text>
                        {(() => {
                          const renewalOffers = getMemberOffers(catalogEntries, fm.user_id);
                          const allowedInactivePlanIds = renewalOffers
                            .filter((o) => o.is_exclusive)
                            .map((o) => o.plan_id);
                          return (
                            <PlanSelector
                              plans={plans}
                              dob={fm.dob}
                              selectedPlanId={renewPlanId}
                              onSelectPlan={setRenewPlanId}
                              variant="row"
                              allowedInactivePlanIds={allowedInactivePlanIds}
                              offers={renewalOffers}
                              showDiscountBadge={true}
                            />
                          );
                        })()}
                        <TouchableOpacity
                          style={[styles.cancelRenewBtn, { borderColor: colors.border }]}
                          onPress={() => { setRenewingUserId(null); setRenewPlanId(null); }}
                          activeOpacity={0.7}
                        >
                          <Text style={[styles.cancelRenewText, { color: colors.textSecondary }]}>{t('common.cancel')}</Text>
                        </TouchableOpacity>
                      </View>
                    )}

                    {/* Confirm new plan (Add Plan case only; renewals confirm inside the plan row) */}
                    {!plan && renewingUserId === fm.user_id && renewPlanId && (
                      <View style={styles.renewConfirm}>
                        <TermSelector
                          selectedTermMonths={renewTerm}
                          onSelectTerm={(months) => setMemberTerms(prev => ({ ...prev, [fm.user_id]: months }))}
                          compact
                          allowedTermMonths={renewSelectedPlan
                            ? getAllowedTermsForMemberPlan(memberOffers, renewSelectedPlan.id)
                            : undefined}
                        />
                        {!renewValidTerm && (
                          <Text style={{ fontSize: 11, color: '#ef4444', alignSelf: 'flex-start', marginTop: -2 }}>
                            {t('billing.selectTermRequired', { defaultValue: 'Please select a term to continue' })}
                          </Text>
                        )}
                        {renewPricing && (
                          <View style={[styles.pricingBreakdown, { backgroundColor: colors.bg, borderColor: colors.border }]}>
                            <View style={styles.pricingBreakdownRow}>
                              <View style={{ flex: 1 }}>
                                <Text style={[styles.pricingBreakdownPlan, { color: colors.text }]}>
                                  {renewSelectedPlan ? t(`plans.${renewSelectedPlan.machine_name}.name`, { defaultValue: renewSelectedPlan.machine_name }) : ''}
                                </Text>
                                <Text style={[styles.pricingBreakdownTerm, { color: colors.textTertiary }]}>
                                  {renewTerm} {renewTerm === 1 ? t('common.month') : t('common.months')}
                                </Text>
                                {renewHasSavings && renewPricing.discount && (
                                  <Text style={[styles.pricingBreakdownDiscount, { color: colors.accent }]}>
                                    {getDiscountText(renewPricing.discount, renewPricing.subtotal, renewPricing.finalAmount, formatCurrency, t, renewTerm)}
                                  </Text>
                                )}
                              </View>
                              <View style={{ alignItems: 'flex-end' }}>
                                {renewHasSavings && (
                                  <Text style={[styles.pricingBreakdownStrike, { color: colors.textTertiary }]}>
                                    {formatCurrency(renewPricing.subtotal)}
                                  </Text>
                                )}
                                <Text style={[styles.pricingBreakdownFinal, { color: colors.text }]}>
                                  {formatCurrency(renewPricing.finalAmount)}
                                </Text>
                                <Text style={[styles.pricingBreakdownPerMonth, { color: colors.textSecondary }]}>
                                  {formatCurrency(renewPricing.finalAmount / renewTerm)}/{t('billing.perMonth')}
                                </Text>
                              </View>
                            </View>
                          </View>
                        )}
                        <TouchableOpacity
                          style={[styles.confirmRenewBtn, { backgroundColor: colors.accent, opacity: !renewValidTerm ? 0.5 : 1 }]}
                          onPress={() => handleQueueRenewal(fm.user_id)}
                          disabled={!renewValidTerm}
                          activeOpacity={0.7}
                        >
                          <Text style={[styles.confirmRenewBtnText, { color: colors.accentText }]}>
                            {t('mgmt.addToPayment')}
                          </Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => { setRenewingUserId(null); setRenewPlanId(null); }}
                          hitSlop={12}
                        >
                          <Text style={[styles.cancelRenewText, { color: colors.textSecondary }]}>{t('common.cancel')}</Text>
                        </TouchableOpacity>
                      </View>
                    )}

                    {/* Pending scheduled change banner */}
                    {isActive && membership?.pending_plan && (
                      <View style={[styles.scheduledBanner, { backgroundColor: 'rgba(59,130,246,0.1)', borderColor: 'rgba(59,130,246,0.3)' }]}>
                        <Icon name="calendar-clock" size={18} color="#3b82f6" />
                        <View style={{ flex: 1 }}>
                          <Text style={[styles.scheduledTitle, { color: '#3b82f6' }]}>
                            {t('mgmt.scheduledChangeTitle', {
                              plan: t(`plans.${membership.pending_plan.machine_name}.name`, { defaultValue: membership.pending_plan.machine_name }),
                            })}
                          </Text>
                          <Text style={[styles.scheduledBody, { color: colors.textSecondary }]}>
                            {t('mgmt.scheduledEndOfTerm')}
                          </Text>
                        </View>
                        <TouchableOpacity onPress={() => handleClearScheduled(membership.id)} hitSlop={8}>
                          <Text style={[styles.scheduledAction, { color: '#3b82f6' }]}>{t('mgmt.undo')}</Text>
                        </TouchableOpacity>
                      </View>
                    )}
                    {isActive && membership?.pending_cancel && (
                      <View style={[styles.scheduledBanner, { backgroundColor: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.3)' }]}>
                        <Icon name="calendar-remove" size={18} color="#ef4444" />
                        <View style={{ flex: 1 }}>
                          <Text style={[styles.scheduledTitle, { color: '#ef4444' }]}>
                            {t('mgmt.scheduledCancelTitle')}
                          </Text>
                          <Text style={[styles.scheduledBody, { color: colors.textSecondary }]}>
                            {t('mgmt.scheduledEndOfTerm')}
                          </Text>
                        </View>
                        <TouchableOpacity onPress={() => handleClearScheduled(membership.id)} hitSlop={8}>
                          <Text style={[styles.scheduledAction, { color: '#ef4444' }]}>{t('mgmt.undo')}</Text>
                        </TouchableOpacity>
                      </View>
                    )}

                    {/* Queued (pending, not yet paid) plan change banner */}
                    {isActive && pendingChanges[membership?.id] && (() => {
                      const queuedPlan = plans?.find((p) => p.id === pendingChanges[membership.id]);
                      const currentPrice = parseFloat(plan.price);
                      const newPrice = queuedPlan ? parseFloat(queuedPlan.price) : 0;
                      const isDowngrade = newPrice <= currentPrice;
                      return (
                        <View style={[styles.scheduledBanner, { backgroundColor: 'rgba(251,191,36,0.12)', borderColor: 'rgba(251,191,36,0.35)' }]}>
                          <Icon name="pencil-outline" size={18} color="#d97706" />
                          <View style={{ flex: 1 }}>
                            <Text style={[styles.scheduledTitle, { color: '#d97706' }]}>
                              {t('mgmt.pendingChange')}
                            </Text>
                            <Text style={[styles.scheduledBody, { color: colors.textSecondary }]}>
                              {t('mgmt.planTransition', {
                                from: t(`plans.${plan.machine_name}.name`, { defaultValue: plan.machine_name }),
                                to: queuedPlan ? t(`plans.${queuedPlan.machine_name}.name`, { defaultValue: queuedPlan.machine_name }) : '',
                              })}
                              {' • '}
                              {isDowngrade ? t('mgmt.downgrade') : t('mgmt.upgrade')}
                            </Text>
                          </View>
                          <TouchableOpacity onPress={() => handleRemovePendingChange(membership.id)} hitSlop={8}>
                            <Text style={[styles.scheduledAction, { color: '#d97706' }]}>{t('mgmt.remove')}</Text>
                          </TouchableOpacity>
                        </View>
                      );
                    })()}

                    {/* Change / Cancel action buttons — only for active memberships without a pending change */}
                    {isActive && !membership?.pending_cancel && !membership?.pending_plan_id && !pendingChanges[membership?.id] && selectorOpenFor !== membership?.id && (
                      <View style={[styles.actionRow, isNarrow && styles.actionRowNarrow]}>
                        <TouchableOpacity
                          style={[styles.secondaryBtn, { borderColor: colors.border }]}
                          onPress={handleContactForChanges}
                          activeOpacity={0.7}
                        >
                          <Icon name="swap-horizontal" size={16} color={colors.text} />
                          <Text style={[styles.secondaryBtnText, { color: colors.text }]}>{t('mgmt.changePlan')}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[styles.secondaryBtn, { borderColor: 'rgba(239,68,68,0.4)' }]}
                          onPress={() => handleCancelMembership(membership.id)}
                          activeOpacity={0.7}
                        >
                          <Icon name="close-circle-outline" size={16} color="#ef4444" />
                          <Text style={[styles.secondaryBtnText, { color: '#ef4444' }]}>{t('mgmt.cancelMembership')}</Text>
                        </TouchableOpacity>
                      </View>
                    )}

                    {/* Change-plan: plan selector (queues the change — no API call here) */}
                    {isActive && selectorOpenFor === membership?.id && (
                      <View style={styles.planSelectorWrap}>
                        <Text style={[styles.planSelectorLabel, { color: colors.textTertiary }]}>
                          {t('mgmt.selectNewPlan')}
                        </Text>
                        {(() => {
                          const changeOffers = getMemberOffers(catalogEntries, fm.user_id);
                          const allowedInactivePlanIds = changeOffers
                            .filter((o) => o.is_exclusive)
                            .map((o) => o.plan_id);
                          return (
                            <PlanSelector
                              plans={plans?.filter((p) => p.id !== membership.plan_id)}
                              dob={fm.dob}
                              selectedPlanId={null}
                              onSelectPlan={(pid) => handleQueueChangePlan(membership.id, pid)}
                              variant="row"
                              allowedInactivePlanIds={allowedInactivePlanIds}
                              offers={changeOffers}
                              showDiscountBadge={true}
                            />
                          );
                        })()}
                        <Text style={[styles.changeHint, { color: colors.textSecondary }]}>
                          {t('mgmt.changeProratedHint')}
                        </Text>
                        <TouchableOpacity
                          style={[styles.cancelRenewBtn, { borderColor: colors.border }]}
                          onPress={() => setSelectorOpenFor(null)}
                          activeOpacity={0.7}
                        >
                          <Text style={[styles.cancelRenewText, { color: colors.textSecondary }]}>{t('common.cancel')}</Text>
                        </TouchableOpacity>
                      </View>
                    )}
                  </View>
                );
              })
            ) : (
              <View style={styles.emptyState}>
                <Icon name="card-account-details-outline" size={48} color={colors.textTertiary} />
                <Text style={[styles.emptyTitle, { color: colors.textSecondary }]}>{t('mgmt.noMemberships')}</Text>
                <Text style={[styles.emptySubtitle, { color: colors.textTertiary }]}>{t('mgmt.noMembershipsDesc')}</Text>
              </View>
            )}

            {/* ══════════ Add Member (inline form or CTA button) ══════════ */}
            {showAddMemberForm ? (
              <View style={[styles.memberCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[styles.planSelectorLabel, { color: colors.textTertiary }]}>
                  {t('mgmt.addMemberPromptTitle')}
                </Text>
                <SubUserForm
                  plans={plans}
                  onSubmit={handleAddMember}
                  onCancel={() => setShowAddMemberForm(false)}
                  isSubmitting={false}
                  submitLabel={t('mgmt.addToQueue', { defaultValue: 'Add to Summary' })}
                  selectPlanLabel={t('mgmt.selectPlan')}
                  offers={(() => {
                  // Determine the draft dummy ID for the Add Member form
                  const allIds = [user?.id, ...(familyMemberships?.map((fm: any) => fm.user_id) || []), ...pendingMembers.map(m => m.id)];
                  const draftId = getNextDummyUUID(allIds);
                  return getMemberOffers(catalogEntries, draftId);
                })()}
                  showTermPricing
                />
              </View>
            ) : (
              <TouchableOpacity
                style={[styles.addMemberInlineBtn, { borderColor: colors.border }]}
                onPress={() => setShowAddMemberForm(true)}
                activeOpacity={0.7}
              >
                <Icon name="account-plus" size={18} color={colors.accent} />
                <Text style={[styles.addMemberInlineText, { color: colors.accent }]}>
                  {t('mgmt.addMember')}
                </Text>
              </TouchableOpacity>
            )}

            {/* ══════════ Review & Pay Summary (queued changes + renewals + new members) ══════════ */}
            {(Object.keys(pendingChanges).length > 0 || pendingMembers.length > 0 || Object.keys(pendingRenewals).length > 0) && (() => {
              const rows = Object.entries(pendingChanges).map(([membershipId, newPlanId]) => {
                const fm = (familyMemberships as any[] | undefined)?.find((f: any) => f?.membership?.id === membershipId);
                const currentPlan = fm?.membership?.plan;
                const newPlan = plans?.find((p) => p.id === newPlanId);
                const oldPrice = currentPlan ? parseFloat(currentPlan.price) : 0;
                const newPrice = newPlan ? parseFloat(newPlan.price) : 0;
                const isDowngrade = newPrice <= oldPrice;
                const termMonths = fm?.membership?.billing_details?.term_months || 1;
                const prorated = isDowngrade
                  ? 0
                  : computeProrated(oldPrice, newPrice, fm?.membership?.start_date, termMonths);
                return {
                  membershipId,
                  memberName: fm?.user_name || '',
                  currentPlan,
                  newPlan,
                  isDowngrade,
                  prorated,
                };
              });
              const memberRows = pendingMembers.map((m) => {
                const plan = plans?.find((p) => p.id === m.planId);
                const price = plan ? parseFloat(plan.price) : 0;
                return { member: m, plan, price };
              });
              const changeTotal = rows.reduce((acc, r) => acc + r.prorated, 0);
              const memberTotal = memberRows.reduce((acc, r) => {
                const term = memberTerms[r.member.id] ?? 0;
                const mOffers = getMemberOffers(catalogEntries, r.member.id);
                const allowedTerms = r.plan ? getAllowedTermsForMemberPlan(mOffers, r.plan.id) : [3, 6, 12];
                if (!allowedTerms.includes(term)) return acc;
                const offer = r.plan ? getOfferForPlanTerm(mOffers, r.plan.id, term) : null;
                const discountedPrice = r.plan ? calculateOfferPrice(r.plan, term, offer).finalAmount : r.price;
                return acc + discountedPrice;
              }, 0);
              // Queued renewals: term and plan are frozen at "Add to payment" time
              const renewalRows = Object.entries(pendingRenewals).map(([rUserId, r]) => {
                const fm = (familyMemberships as any[] | undefined)?.find((f: any) => f?.user_id === rUserId);
                const plan = plans?.find((p) => p.id === r.planId);
                const rOffers = getMemberOffers(catalogEntries, rUserId);
                // A queued renewal whose plan is no longer offered is invalid (disables Pay)
                const validTerm = isRenewalTermValid(!!plan, catalogEntries, rUserId, r.planId, r.termMonths);
                const offer = plan && validTerm ? getOfferForPlanTerm(rOffers, plan.id, r.termMonths) : null;
                const pricing = plan && validTerm ? calculateOfferPrice(plan, r.termMonths, offer) : null;
                return {
                  userId: rUserId,
                  memberName: fm?.user_name || '',
                  early: isEarlyRenewal(fm?.membership, new Date()),
                  plan,
                  termMonths: r.termMonths,
                  validTerm,
                  pricing,
                };
              });
              const renewalTotal = basketTotal(
                renewalRows.flatMap((r) => (r.pricing ? [r.pricing] : [])),
              );
              const total = changeTotal + memberTotal + renewalTotal;
              const hasUpgrade = rows.some((r) => !r.isDowngrade);
              const hasMembers = memberRows.length > 0;
              const hasRenewals = renewalRows.length > 0;
              const hasCharge = hasUpgrade || hasMembers || hasRenewals;
              const submitting = batchChangePlans.isPending || initiateOrder.isPending;
              const allTermsSelected = memberRows.every((r) => {
                const mOffers = getMemberOffers(catalogEntries, r.member.id);
                const allowedTerms = r.plan ? getAllowedTermsForMemberPlan(mOffers, r.plan.id) : [3, 6, 12];
                return allowedTerms.includes(memberTerms[r.member.id] ?? 0);
              }) && renewalRows.every((r) => r.validTerm);
              const canPay = !submitting && allTermsSelected;

              return (
                <View style={[styles.summaryCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
                  <Text style={[styles.summaryTitle, { color: colors.text }]}>{t('mgmt.pendingChangesTitle')}</Text>
                  <Text style={[styles.summarySubtitle, { color: colors.textSecondary }]}>
                    {t('mgmt.pendingChangesSubtitle')}
                  </Text>

                  <View style={styles.summaryRows}>
                    {rows.map((r) => (
                      <View key={r.membershipId} style={[styles.summaryRow, { borderBottomColor: colors.border }]}>
                        <View style={{ flex: 1 }}>
                          <Text style={[styles.summaryMember, { color: colors.text }]}>{r.memberName}</Text>
                          <Text style={[styles.summaryTransition, { color: colors.textSecondary }]}>
                            {t('mgmt.planTransition', {
                              from: r.currentPlan ? t(`plans.${r.currentPlan.machine_name}.name`, { defaultValue: r.currentPlan.machine_name }) : '',
                              to: r.newPlan ? t(`plans.${r.newPlan.machine_name}.name`, { defaultValue: r.newPlan.machine_name }) : '',
                            })}
                          </Text>
                          <Text style={[styles.summaryKind, { color: r.isDowngrade ? colors.textTertiary : colors.accent }]}>
                            {r.isDowngrade ? t('mgmt.downgrade') : t('mgmt.upgrade')}
                          </Text>
                        </View>
                        <View style={{ alignItems: 'flex-end' }}>
                          <Text style={[styles.summaryAmount, { color: colors.text }]}>
                            {r.isDowngrade ? '—' : formatCurrency(r.prorated)}
                          </Text>
                          <TouchableOpacity onPress={() => handleRemovePendingChange(r.membershipId)} hitSlop={8}>
                            <Text style={[styles.summaryRemove, { color: '#ef4444' }]}>{t('mgmt.remove')}</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    ))}
                    {memberRows.map((r) => {
                      const term = memberTerms[r.member.id] ?? 0;
                      const mOffers = getMemberOffers(catalogEntries, r.member.id);
                      const allowedTerms = r.plan ? getAllowedTermsForMemberPlan(mOffers, r.plan.id) : [3, 6, 12];
                      const validTerm = allowedTerms.includes(term);
                      const offer = r.plan && validTerm ? getOfferForPlanTerm(mOffers, r.plan.id, term) : null;
                      const pricing = r.plan && validTerm ? calculateOfferPrice(r.plan, term, offer) : null;
                      const finalPrice = pricing ? pricing.finalAmount : r.price;
                      const hasSavings = pricing ? pricing.discountAmount > 0 : false;
                      return (
                        <View key={r.member.id} style={[styles.summaryRow, { borderBottomColor: colors.border }]}>
                          <View style={{ flex: 1 }}>
                            <Text style={[styles.summaryMember, { color: colors.text }]}>
                              {r.member.firstName} {r.member.lastName}
                            </Text>
                            <Text style={[styles.summaryTransition, { color: colors.textSecondary }]}>
                              {r.plan ? t(`plans.${r.plan.machine_name}.name`, { defaultValue: r.plan.machine_name }) : ''}
                            </Text>
                            <Text style={[styles.summaryKind, { color: colors.accent }]}>
                              {t('mgmt.newMember', { defaultValue: 'New Member' })}
                            </Text>
                            {/* Term selector */}
                            <TermSelector
                              selectedTermMonths={term}
                              onSelectTerm={(months) => setMemberTerms(prev => ({ ...prev, [r.member.id]: months }))}
                              compact
                              allowedTermMonths={r.plan
                                ? getAllowedTermsForMemberPlan(mOffers, r.plan.id)
                                : undefined}
                            />
                            {!validTerm && (
                              <Text style={{ fontSize: 11, color: '#ef4444', marginTop: -2, marginBottom: 4 }}>
                                {t('billing.selectTermRequired', { defaultValue: 'Please select a term to continue' })}
                              </Text>
                            )}
                            {hasSavings && pricing?.discount && (
                              <Text style={[styles.summaryDiscount, { color: colors.accent }]}>
                                {getDiscountText(pricing.discount, pricing.subtotal, pricing.finalAmount, formatCurrency, t, term)}
                              </Text>
                            )}
                          </View>
                          <View style={{ alignItems: 'flex-end' }}>
                            {hasSavings && pricing && (
                              <Text style={[styles.summaryAmountStrike, { color: colors.textTertiary }]}>
                                {formatCurrency(pricing.subtotal)}
                              </Text>
                            )}
                            <Text style={[styles.summaryAmount, { color: colors.text }]}>
                              {validTerm ? formatCurrency(finalPrice) : '—'}
                            </Text>
                            {validTerm && pricing && (
                              <Text style={[styles.summaryPerMonth, { color: colors.textSecondary }]}>
                                {formatCurrency(pricing.finalAmount / term)}/{t('billing.perMonth')}
                              </Text>
                            )}
                            <TouchableOpacity onPress={() => handleRemovePendingMember(r.member.id)} hitSlop={8}>
                              <Text style={[styles.summaryRemove, { color: '#ef4444' }]}>{t('mgmt.remove')}</Text>
                            </TouchableOpacity>
                          </View>
                        </View>
                      );
                    })}
                    {hasRenewals && (
                      <Text style={[styles.summaryGroupTitle, { color: colors.textTertiary }]}>
                        {t('mgmt.renewalsSection')}
                      </Text>
                    )}
                    {renewalRows.map((r) => {
                      const hasSavings = r.pricing ? r.pricing.discountAmount > 0 : false;
                      return (
                        <View key={`renew-${r.userId}`} style={[styles.summaryRow, { borderBottomColor: colors.border }]}>
                          <View style={{ flex: 1 }}>
                            <Text style={[styles.summaryMember, { color: colors.text }]}>{r.memberName}</Text>
                            <Text style={[styles.summaryTransition, { color: colors.textSecondary }]}>
                              {r.plan ? t(`plans.${r.plan.machine_name}.name`, { defaultValue: r.plan.machine_name }) : ''}
                              {' / '}
                              {r.termMonths} {r.termMonths === 1 ? t('common.month') : t('common.months')}
                            </Text>
                            <Text style={[styles.summaryKind, { color: colors.accent }]}>
                              {r.early ? t('mgmt.renewEarly') : t('mgmt.renew')}
                            </Text>
                            {!r.validTerm && (
                              <Text style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>
                                {t('billing.selectTermRequired', { defaultValue: 'Please select a term to continue' })}
                              </Text>
                            )}
                            {hasSavings && r.pricing?.discount && (
                              <Text style={[styles.summaryDiscount, { color: colors.accent }]}>
                                {getDiscountText(r.pricing.discount, r.pricing.subtotal, r.pricing.finalAmount, formatCurrency, t, r.termMonths)}
                              </Text>
                            )}
                          </View>
                          <View style={{ alignItems: 'flex-end' }}>
                            {hasSavings && r.pricing && (
                              <Text style={[styles.summaryAmountStrike, { color: colors.textTertiary }]}>
                                {formatCurrency(r.pricing.subtotal)}
                              </Text>
                            )}
                            <Text style={[styles.summaryAmount, { color: colors.text }]}>
                              {r.pricing ? formatCurrency(r.pricing.finalAmount) : '-'}
                            </Text>
                            {r.pricing && r.termMonths > 0 && (
                              <Text style={[styles.summaryPerMonth, { color: colors.textSecondary }]}>
                                {formatCurrency(r.pricing.finalAmount / r.termMonths)}/{t('billing.perMonth')}
                              </Text>
                            )}
                            <TouchableOpacity onPress={() => handleRemovePendingRenewal(r.userId)} hitSlop={8}>
                              <Text style={[styles.summaryRemove, { color: '#ef4444' }]}>{t('mgmt.remove')}</Text>
                            </TouchableOpacity>
                          </View>
                        </View>
                      );
                    })}
                  </View>

                  <View style={[styles.summaryTotalRow, { borderTopColor: colors.border }]}>
                    <Text style={[styles.summaryTotalLabel, { color: colors.text }]}>
                      {t('mgmt.totalProrated')}
                    </Text>
                    <Text style={[styles.summaryTotalValue, { color: colors.text }]}>
                      {hasCharge ? formatCurrency(total) : '—'}
                    </Text>
                  </View>

                  {!hasCharge && (
                    <Text style={[styles.changeHint, { color: colors.textSecondary }]}>
                      {t('mgmt.noUpgradesScheduled')}
                    </Text>
                  )}
                  {hasCharge && (
                    <Text style={[styles.changeHint, { color: colors.textSecondary }]}>
                      {t('mgmt.totalEstimateHint')}
                    </Text>
                  )}

                  <TouchableOpacity
                    style={[styles.confirmRenewBtn, { backgroundColor: colors.accent, opacity: canPay ? 1 : 0.5, marginTop: 12 }]}
                    onPress={handlePayPendingChanges}
                    disabled={!canPay}
                    activeOpacity={0.7}
                  >
                    {submitting ? (
                      <ActivityIndicator size="small" color={colors.accentText} />
                    ) : (
                      <Text style={[styles.confirmRenewBtnText, { color: colors.accentText }]}>
                        {hasCharge
                          ? t('billing.payAmount', { amount: formatCurrency(total) })
                          : t('mgmt.confirmChanges')}
                      </Text>
                    )}
                  </TouchableOpacity>
                </View>
              );
            })()}
          </View>
        )}

        {activeTab === 'payments' && (
          /* ══════════════ PAYMENTS TAB ══════════════ */
          <View style={styles.tabContent}>
            <PaymentHistoryList
              payments={paymentHistory as any}
              isLoading={paymentsLoading}
            />
          </View>
        )}
        </ScrollView>
      </KeyboardAvoidingView>

      {/* Contact Dialog */}
      <Modal
        visible={showContactDialog}
        transparent
        animationType="fade"
        onRequestClose={() => setShowContactDialog(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowContactDialog(false)}
        >
          <TouchableOpacity
            style={[styles.modalCard, { backgroundColor: colors.card, borderColor: colors.border }]}
            activeOpacity={1}
          >
            <View style={[styles.modalIconContainer, { backgroundColor: colors.accent + '15' }]}>
              <Icon name="information-outline" size={32} color={colors.accent} />
            </View>

            <Text style={[styles.modalTitle, { color: colors.text }]}>
              {t('mgmt.contactForChangesTitle')}
            </Text>

            <Text style={[styles.modalDesc, { color: colors.textSecondary }]}>
              {t('mgmt.contactForChangesMessage')}
            </Text>

            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.modalPrimaryBtn, { backgroundColor: colors.accent }]}
                activeOpacity={0.8}
                onPress={() => setShowContactDialog(false)}
              >
                <Text style={[styles.modalPrimaryBtnText, { color: colors.accentText }]}>
                  {t('common.ok')}
                </Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  scrollView: { flex: 1 },
  scrollContent: { padding: 20, gap: 16 },
  tabContent: { gap: 12 },

  // Tab bar
  tabBar: {
    borderBottomWidth: 1,
  },
  tabBarInner: {
    flexDirection: 'row',
  },
  tabItem: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabLabel: { fontSize: 13, fontWeight: '600' },

  // Member card
  memberCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    gap: 12,
  },
  memberHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  memberAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  memberInfo: { flex: 1, gap: 2 },
  memberName: { fontSize: 15, fontWeight: '600' },
  memberDob: { fontSize: 12 },

  // Plan row
  planRow: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 4,
  },
  planName: { fontSize: 14, fontWeight: '600' },
  planMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 2,
  },
  planPrice: { fontSize: 13 },
  planDate: { fontSize: 11, marginTop: 2 },
  planDiscount: {
    fontSize: 11,
    fontWeight: '500',
    marginTop: 2,
  },
  discountChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    alignSelf: 'flex-start',
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 6,
    marginTop: 4,
  },
  discountChipText: { fontSize: 11, fontWeight: '600' },
  priceCol: {
    alignItems: 'flex-end',
  },
  priceStrike: {
    fontSize: 10,
    textDecorationLine: 'line-through',
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
  },
  statusDot: { width: 6, height: 6, borderRadius: 3 },
  statusText: { fontSize: 10, fontWeight: '700' },

  // No plan row
  noPlanRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
  },
  noPlanText: { flex: 1, fontSize: 13 },
  addPlanBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  addPlanBtnText: { fontSize: 12, fontWeight: '600' },

  // Renew
  renewBtn: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  renewBtnText: { fontSize: 13, fontWeight: '600' },
  queuedRenewal: { alignItems: 'flex-end', gap: 2 },
  queuedRenewalText: { fontSize: 12, fontWeight: '600' },
  summaryGroupTitle: { fontSize: 12, fontWeight: '600', letterSpacing: 0.5, textTransform: 'uppercase', marginTop: 10 },
  planSelectorWrap: {
    gap: 8,
    marginTop: 4,
  },
  planSelectorLabel: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  cancelRenewBtn: {
    paddingVertical: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 6,
  },
  cancelRenewText: { fontSize: 13, fontWeight: '500' },
  renewConfirm: {
    gap: 8,
    alignItems: 'center',
    marginTop: 4,
  },
  confirmRenewBtn: {
    width: '100%',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  confirmRenewBtnText: { fontSize: 15, fontWeight: '600' },

  // Term selector buttons
  termButton: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    borderWidth: 1,
    minWidth: 55,
    alignItems: 'center',
  },
  termButtonText: {
    fontSize: 11,
    fontWeight: '600',
  },

  // Add member prompt
  addMemberPrompt: {
    alignItems: 'center',
    paddingVertical: 48,
    gap: 12,
  },
  addMemberPromptTitle: { fontSize: 16, fontWeight: '600' },
  addMemberPromptDesc: { fontSize: 13, textAlign: 'center', lineHeight: 20 },
  addMemberPromptBtn: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 8,
  },
  addMemberPromptBtnText: { fontSize: 14, fontWeight: '600' },

  // Inline Add Member button (in Memberships tab)
  addMemberInlineBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: 'dashed',
  },
  addMemberInlineText: { fontSize: 14, fontWeight: '600' },

  // Empty state
  emptyState: {
    alignItems: 'center',
    paddingVertical: 48,
    gap: 8,
  },
  emptyTitle: { fontSize: 16, fontWeight: '600', marginTop: 12 },
  emptySubtitle: { fontSize: 13, textAlign: 'center', lineHeight: 20 },

  // Scheduled change banner
  scheduledBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: 8,
    borderWidth: 1,
    padding: 10,
  },
  scheduledTitle: { fontSize: 13, fontWeight: '700' },
  scheduledBody: { fontSize: 11, marginTop: 2 },
  scheduledAction: { fontSize: 13, fontWeight: '600' },

  // Action row (change / cancel)
  actionRow: {
    flexDirection: 'row',
    gap: 8,
  },
  actionRowNarrow: {
    flexDirection: 'column',
  },
  secondaryBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  secondaryBtnText: { fontSize: 13, fontWeight: '600' },

  // Change-plan hint
  changeHint: { fontSize: 12, fontStyle: 'italic', marginTop: 4 },

  // Pricing breakdown card (renew confirm)
  pricingBreakdown: {
    width: '100%',
    borderRadius: 8,
    borderWidth: 1,
    padding: 10,
  },
  pricingBreakdownRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  pricingBreakdownPlan: { fontSize: 13, fontWeight: '600' },
  pricingBreakdownTerm: { fontSize: 11, marginTop: 2 },
  pricingBreakdownDiscount: { fontSize: 11, fontWeight: '500', marginTop: 2 },
  pricingBreakdownStrike: { fontSize: 11, textDecorationLine: 'line-through' },
  pricingBreakdownFinal: { fontSize: 15, fontWeight: '700' },
  pricingBreakdownPerMonth: { fontSize: 11, marginTop: 1 },

  // Per-month rate in summary rows
  summaryPerMonth: { fontSize: 11, marginTop: 1 },

  // Review & Pay summary card (queued changes)
  summaryCard: {
    marginTop: 8,
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    gap: 8,
  },
  summaryTitle: { fontSize: 16, fontWeight: '700' },
  summarySubtitle: { fontSize: 12, lineHeight: 18, marginBottom: 4 },
  summaryRows: { marginTop: 4 },
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 10,
    borderBottomWidth: 1,
    gap: 12,
  },
  summaryMember: { fontSize: 14, fontWeight: '600' },
  summaryTransition: { fontSize: 12, marginTop: 2 },
  summaryKind: { fontSize: 11, fontWeight: '700', marginTop: 4, textTransform: 'uppercase', letterSpacing: 0.5 },
  summaryAmountStrike: { fontSize: 10, textDecorationLine: 'line-through' },
  summaryAmount: { fontSize: 14, fontWeight: '700' },
  summaryDiscount: { fontSize: 11, fontWeight: '500', marginTop: 2 },
  summaryRemove: { fontSize: 12, fontWeight: '600', marginTop: 4 },
  summaryTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 12,
    marginTop: 4,
    borderTopWidth: 1,
  },
  summaryTotalLabel: { fontSize: 14, fontWeight: '700' },
  summaryTotalValue: { fontSize: 18, fontWeight: '700' },

  // Contact dialog modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalCard: {
    width: '100%',
    maxWidth: 340,
    borderRadius: 20,
    borderWidth: 1,
    padding: 24,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  modalIconContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  modalDesc: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  modalActions: {
    width: '100%',
    gap: 8,
  },
  modalPrimaryBtn: {
    width: '100%',
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalPrimaryBtnText: {
    fontSize: 15,
    fontWeight: '700',
  },
});
